import { useMemo } from "react";

import { formatEventTimeLocal } from "../lib/dashboardTime.js";
import { gameColorFromName, shellScreenColor } from "../lib/gamePalette.js";
import { useReportingTimezone } from "../lib/ReportingTimezoneContext.js";
import type { SessionJourneyData, SessionJourneyPaymentConfirmed, SessionJourneySegment } from "../types.js";
import { ChartCard } from "./ChartCard.js";

function fmtMs(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  const h = Math.floor(ms / 3_600_000);
  const m = Math.round((ms % 3_600_000) / 60_000);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function fmtAxisOffset(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) {
    const m = ms / 60_000;
    return Number.isInteger(m) ? `${m}m` : `${m.toFixed(1)}m`;
  }
  const h = ms / 3_600_000;
  return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`;
}

export function segmentFill(segment: SessionJourneySegment): string {
  const shell = shellScreenColor(segment.screenName);
  if (shell) return shell;
  if (segment.screenName === "game" && segment.gameName) return gameColorFromName(segment.gameName);
  return "#5a6a8a";
}

export interface SegmentRawEventsFilters {
  eventName: string;
  propertiesContains: string;
  propertiesContains2: string;
}

export function segmentRawEventsFilters(segment: SessionJourneySegment): SegmentRawEventsFilters {
  if (segment.inProgress) {
    if (segment.screenName === "game" && segment.gameName) {
      return {
        eventName: "all",
        propertiesContains: `"game_name":"${segment.gameName}"`,
        propertiesContains2: "",
      };
    }
    return {
      eventName: "screen_viewed",
      propertiesContains: `"screen_name":"${segment.screenName}"`,
      propertiesContains2: "",
    };
  }
  if (segment.screenName === "game" && segment.gameName) {
    return {
      eventName: "all",
      propertiesContains: `"game_name":"${segment.gameName}"`,
      propertiesContains2: "",
    };
  }
  return {
    eventName: "screen_exited",
    propertiesContains: `"screen_name":"${segment.screenName}"`,
    propertiesContains2: `"duration_ms":${segment.durationMs}`,
  };
}

function segmentClickHint(segment: SessionJourneySegment): string {
  if (segment.inProgress) {
    if (segment.screenName === "game" && segment.gameName) {
      return `In progress. Click: Raw Events → all events with game_name ${segment.gameName} in this session.`;
    }
    return "In progress. Click: Raw Events → matching screen_viewed in this session.";
  }
  if (segment.screenName === "game" && segment.gameName) {
    return `Click: Raw Events → all events with game_name ${segment.gameName} in this session.`;
  }
  return "Click: Raw Events → matching screen_exited stint in this session.";
}

interface SessionJourneyBarProps {
  journey: SessionJourneyData;
  selectedSegmentKey: string | null;
  onSegmentClick: (segment: SessionJourneySegment, filters: SegmentRawEventsFilters) => void;
  onPaymentConfirmedClick?: (paidSessionId: string | null) => void;
  /** @deprecated use onPaymentConfirmedClick */
  onPaymentSucceededClick?: () => void;
}

function segmentKey(segment: SessionJourneySegment): string {
  return `${segment.startTs}|${segment.screenName}|${segment.durationMs}|${segment.gameName ?? ""}`;
}

function axisTickIntervalMs(spanMs: number): number {
  const five = 5 * 60_000;
  const ten = 10 * 60_000;
  const thirty = 30 * 60_000;
  const hour = 60 * 60_000;
  if (spanMs <= 25 * 60_000) return five;
  if (spanMs <= 2 * 3600_000) return ten;
  if (spanMs <= 6 * 3600_000) return thirty;
  return hour;
}

function axisTicks(spanMs: number): number[] {
  const step = axisTickIntervalMs(spanMs);
  const ticks: number[] = [0];
  for (let t = step; t < spanMs; t += step) ticks.push(t);
  return ticks;
}

/** Drawable wall span: first screen_exited exit (or open-segment start) → last exit / payment. */
export function journeyTimelineBounds(
  journey: Pick<
    SessionJourneyData,
    | "segments"
    | "paymentConfirmed"
    | "paymentConfirmedAt"
    | "paymentSucceededAt"
    | "startTs"
    | "endTs"
    | "spanMs"
  >,
): { startMs: number; endMs: number; spanMs: number } {
  const paymentMarkers: SessionJourneyPaymentConfirmed[] =
    journey.paymentConfirmed.length > 0
      ? journey.paymentConfirmed
      : (journey.paymentConfirmedAt.length > 0
          ? journey.paymentConfirmedAt
          : journey.paymentSucceededAt
        ).map((at) => ({ at, durationMinutes: 0, paidSessionId: null }));

  let startMs: number | null = null;
  let endMs: number | null = null;

  if (journey.segments.length > 0) {
    const first = journey.segments[0]!;
    const last = journey.segments[journey.segments.length - 1]!;
    // Open-only (or leading open) segments anchor at screen_viewed start, not an exit time.
    startMs = first.inProgress
      ? new Date(first.startTs).getTime()
      : new Date(first.endTs).getTime();
    endMs = new Date(last.endTs).getTime();

    if (journey.segments.length === 1 || endMs <= startMs) {
      const seg = first;
      const end = new Date(seg.endTs).getTime();
      const rawStart = new Date(seg.startTs).getTime();
      const maxBackMs = 30 * 60_000;
      startMs = Math.max(rawStart, end - maxBackMs);
      endMs = end;
    }

    for (const p of paymentMarkers) {
      const t = new Date(p.at).getTime();
      endMs = Math.max(endMs, t);
      if (p.durationMinutes > 0) {
        endMs = Math.max(endMs, t + p.durationMinutes * 60_000);
      }
    }
  } else {
    for (const p of paymentMarkers) {
      const t = new Date(p.at).getTime();
      startMs = startMs == null ? t : Math.min(startMs, t);
      endMs = endMs == null ? t : Math.max(endMs, t);
      if (p.durationMinutes > 0) {
        endMs = Math.max(endMs, t + p.durationMinutes * 60_000);
      }
    }
  }

  if (startMs == null || endMs == null) {
    startMs = journey.startTs ? new Date(journey.startTs).getTime() : 0;
    endMs = journey.endTs ? new Date(journey.endTs).getTime() : startMs;
  }

  const spanMs = Math.max(0, endMs - startMs);
  return { startMs, endMs, spanMs: spanMs > 0 ? spanMs : Math.max(journey.spanMs, 1) };
}

export function SessionJourneyBar({
  journey,
  selectedSegmentKey,
  onSegmentClick,
  onPaymentConfirmedClick,
  onPaymentSucceededClick,
}: SessionJourneyBarProps) {
  const paymentMarkers: SessionJourneyPaymentConfirmed[] =
    journey.paymentConfirmed.length > 0
      ? journey.paymentConfirmed
      : (journey.paymentConfirmedAt.length > 0
          ? journey.paymentConfirmedAt
          : journey.paymentSucceededAt
        ).map((at) => ({ at, durationMinutes: 0, paidSessionId: journey.paidSessionId }));
  const reportingTz = useReportingTimezone();
  const { startMs: timelineStart, endMs: timelineEndMs, spanMs: timelineSpan } = useMemo(
    () => journeyTimelineBounds(journey),
    [journey],
  );
  const displayStartTs = new Date(timelineStart).toISOString();
  const displayEndTs = new Date(timelineEndMs).toISOString();
  const ticks = useMemo(() => axisTicks(timelineSpan), [timelineSpan]);

  const legendItems = useMemo(() => {
    const seen = new Map<string, { label: string; fill: string }>();
    for (const seg of journey.segments) {
      const key = seg.screenName === "game" && seg.gameName ? `game:${seg.gameName}` : seg.screenName;
      if (!seen.has(key)) {
        seen.set(key, { label: seg.label, fill: segmentFill(seg) });
      }
    }
    return [...seen.values()];
  }, [journey.segments]);

  const criteria =
    journey.criteria ??
    "screen_exited rows for this interaction_session_id; segment width ∝ duration_ms on wall timeline; gaps between segments are idle/unattributed time. Trailing screen_viewed with no matching exit draws a hatched in-progress segment to the stint's latest event (or now when recent).";

  const hasTimeline = journey.segments.length > 0 || paymentMarkers.length > 0;
  const paymentCount = paymentMarkers.length;
  const hasPaidDurationBar = paymentMarkers.some((p) => p.durationMinutes > 0);
  const showInProgressBadge = Boolean(journey.inProgress);

  return (
    <ChartCard
      title="Session journey"
      criteria={`${criteria}

💵 markers: client_timestamp of each payment_confirmed row linked to this stint via shared play session id (paid_session_id / mobile interactive_session_id), or device + −2 min…+10 min when table payment_succeeded is missing (shell telemetry gap). Thin green bar below = properties.duration_minutes purchased slot from each confirm.

Segment click: game stints → Raw Events with session id + properties contains game_name (all event types). Menu/attract → screen_exited + screen_name + duration_ms (in-progress → screen_viewed). Payment marker click → Raw Events → payment_confirmed filtered by paid_session_id (clears interaction session id — mobile rows lack games ISID).`}
      note={
        paymentCount > 0
          ? `${paymentCount === 1 ? "1 payment" : `${paymentCount} payments`} 💵 on timeline${
              journey.paidSessionId ? ` · paid session ${journey.paidSessionId.slice(0, 8)}…` : ""
            } · ${journey.segments.length} shell stints · ${fmtMs(journey.activeMs)} active dwell`
          : journey.paidSessionId
            ? `Paid session ${journey.paidSessionId.slice(0, 8)}… · ${journey.segments.length} shell stints · ${fmtMs(journey.activeMs)} active dwell`
            : `${journey.segments.length} shell stints · ${fmtMs(journey.activeMs)} active dwell`
      }
      titleRight={
        showInProgressBadge ? (
          <span
            className="inline-flex items-center gap-1 rounded border border-[#31c48d]/50 bg-[#31c48d]/15 px-1.5 py-0.5 text-[11px] font-medium text-[#31c48d]"
            title="Last event within ~5 minutes and current screen is not attract"
          >
            <span
              className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[#31c48d]"
              aria-hidden
            />
            In progress
          </span>
        ) : undefined
      }
    >
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2 text-[12px]">
        <div className="text-text-primary">
          <span className="text-text-secondary">Start </span>
          {displayStartTs ? formatEventTimeLocal(displayStartTs, reportingTz) : "—"}
        </div>
        <div className="font-mono text-[11px] text-text-secondary" title={journey.interactionSessionId}>
          {journey.interactionSessionId.length > 28
            ? `${journey.interactionSessionId.slice(0, 12)}…${journey.interactionSessionId.slice(-10)}`
            : journey.interactionSessionId}
        </div>
        <div className="text-text-primary">
          <span className="text-text-secondary">End </span>
          {displayEndTs ? formatEventTimeLocal(displayEndTs, reportingTz) : "—"}
          <span className="ml-2 text-text-secondary">({fmtMs(timelineSpan)} wall)</span>
        </div>
      </div>

      {hasTimeline ? (
        <>
          <div className="relative">
            <div
              className="relative h-10 w-full overflow-hidden rounded-t-lg border border-b-0 border-border-subtle bg-[#15253f]"
              role="img"
              aria-label="Session shell dwell timeline"
            >
              {journey.segments.map((seg) => {
                const segStartMs = new Date(seg.startTs).getTime();
                const segEndMs = new Date(seg.endTs).getTime();
                const clipStartMs = Math.max(segStartMs, timelineStart);
                const visibleMs = Math.max(0, segEndMs - clipStartMs);
                const leftPct = ((clipStartMs - timelineStart) / timelineSpan) * 100;
                const widthPct = (visibleMs / timelineSpan) * 100;
                const key = segmentKey(seg);
                const selected = selectedSegmentKey === key;
                const fill = segmentFill(seg);
                const minWidth = widthPct < 0.4 ? 0.4 : widthPct;

                if (visibleMs <= 0) return null;

                return (
                  <button
                    key={key}
                    type="button"
                    className="absolute top-0 h-full min-w-[2px] cursor-pointer border-r border-[#1f3355]/80 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-2"
                    style={{
                      left: `${leftPct}%`,
                      width: `${minWidth}%`,
                      backgroundColor: fill,
                      opacity: selected ? 1 : seg.inProgress ? 0.95 : 0.88,
                      boxShadow: selected ? `inset 0 0 0 2px #fff` : undefined,
                      backgroundImage: seg.inProgress
                        ? `repeating-linear-gradient(
                            -45deg,
                            transparent,
                            transparent 3px,
                            rgba(255,255,255,0.22) 3px,
                            rgba(255,255,255,0.22) 6px
                          )`
                        : undefined,
                    }}
                    title={`${seg.label}${seg.inProgress ? " (in progress)" : ""}: ${fmtMs(seg.durationMs)} (${formatEventTimeLocal(seg.startTs, reportingTz)} → ${formatEventTimeLocal(seg.endTs, reportingTz)}). ${segmentClickHint(seg)}`}
                    aria-label={`${seg.label}${seg.inProgress ? ", in progress" : ""}, ${fmtMs(seg.durationMs)}`}
                    onClick={() => onSegmentClick(seg, segmentRawEventsFilters(seg))}
                  />
                );
              })}
              {paymentMarkers.map((payment, i) => {
                const offsetMs = new Date(payment.at).getTime() - timelineStart;
                const leftPct = Math.min(100, Math.max(0, (offsetMs / timelineSpan) * 100));
                const timeLabel = formatEventTimeLocal(payment.at, reportingTz);
                const durationLabel =
                  payment.durationMinutes > 0 ? ` · ${payment.durationMinutes}m purchased` : "";
                const title = `Payment confirmed at ${timeLabel}${durationLabel}${
                  onPaymentConfirmedClick || onPaymentSucceededClick
                    ? " — click: Raw Events → payment_confirmed for this paid_session_id"
                    : ""
                }`;

                return (
                  <button
                    key={`payment-${payment.at}-${i}`}
                    type="button"
                    className="absolute top-0 z-10 flex h-full w-4 -translate-x-1/2 cursor-pointer flex-col items-center border-0 bg-transparent p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#31c48d]"
                    style={{ left: `${leftPct}%` }}
                    title={title}
                    aria-label={`Payment confirmed at ${timeLabel}${durationLabel}`}
                    onClick={() =>
                      onPaymentConfirmedClick
                        ? onPaymentConfirmedClick(payment.paidSessionId ?? journey.paidSessionId)
                        : onPaymentSucceededClick?.()
                    }
                  >
                    <span className="text-[13px] leading-none drop-shadow-sm" aria-hidden>
                      💵
                    </span>
                    <span
                      className="mt-0.5 w-0.5 flex-1 rounded-full bg-[#31c48d]/90"
                      aria-hidden
                    />
                  </button>
                );
              })}
            </div>

            {hasPaidDurationBar ? (
              <div
                className="relative h-2 w-full overflow-hidden border-x border-border-subtle bg-[#101c30]"
                aria-hidden
              >
                {paymentMarkers.map((payment, i) => {
                  if (payment.durationMinutes <= 0) return null;
                  const startMs = new Date(payment.at).getTime() - timelineStart;
                  const durationMs = payment.durationMinutes * 60_000;
                  const leftPct = Math.min(100, Math.max(0, (startMs / timelineSpan) * 100));
                  const widthPct = Math.min(100 - leftPct, (durationMs / timelineSpan) * 100);
                  const minWidth = widthPct < 0.35 ? 0.35 : widthPct;
                  return (
                    <div
                      key={`paid-slot-${payment.at}-${i}`}
                      className="absolute top-0.5 h-1 rounded-sm bg-[#22c55e]/85"
                      style={{ left: `${leftPct}%`, width: `${minWidth}%` }}
                      title={`${payment.durationMinutes}m purchased from payment_confirmed`}
                    />
                  );
                })}
              </div>
            ) : null}

            <div
              className={`relative h-5 w-full border border-t-0 border-border-subtle bg-[#101c30] ${hasPaidDurationBar ? "rounded-b-lg" : "rounded-b-lg"}`}
              aria-hidden
            >
              <div className="absolute inset-x-0 top-0 h-px bg-border-subtle/80" />
              {ticks.map((offsetMs) => {
                const leftPct = (offsetMs / timelineSpan) * 100;
                return (
                  <div
                    key={offsetMs}
                    className="absolute top-0 flex -translate-x-1/2 flex-col items-center"
                    style={{ left: `${leftPct}%` }}
                  >
                    <div className="h-1.5 w-px bg-text-secondary/50" />
                    <span className="mt-0.5 text-[9px] tabular-nums text-text-secondary">
                      {fmtAxisOffset(offsetMs)}
                    </span>
                  </div>
                );
              })}
              <div
                className="absolute top-0 flex -translate-x-full flex-col items-end pr-0.5"
                style={{ left: "100%" }}
              >
                <div className="h-1.5 w-px bg-text-secondary/50" />
                <span className="mt-0.5 text-[9px] tabular-nums text-text-secondary">
                  {fmtAxisOffset(timelineSpan)}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-text-secondary">
            {paymentCount > 0 ? (
              <span className="inline-flex items-center gap-1">
                <span className="text-[12px] leading-none" aria-hidden>
                  💵
                </span>
                Payment confirmed
                {hasPaidDurationBar ? (
                  <>
                    <span className="inline-block h-1 w-3 rounded-sm bg-[#22c55e]/85" aria-hidden />
                    <span>purchased slot</span>
                  </>
                ) : null}
              </span>
            ) : null}
            {legendItems.map((item) => (
              <span key={item.label} className="inline-flex items-center gap-1">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ backgroundColor: item.fill }}
                />
                {item.label}
              </span>
            ))}
            {journey.segments.some((s) => s.inProgress) ? (
              <span className="inline-flex items-center gap-1">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{
                    backgroundColor: "#5a6a8a",
                    backgroundImage: `repeating-linear-gradient(
                      -45deg,
                      transparent,
                      transparent 2px,
                      rgba(255,255,255,0.35) 2px,
                      rgba(255,255,255,0.35) 4px
                    )`,
                  }}
                  aria-hidden
                />
                In progress
              </span>
            ) : null}
          </div>
        </>
      ) : (
        <p className="text-[12px] text-text-secondary">
          No screen_exited, open screen_viewed, or payment_confirmed rows for this session in the
          current date range.
        </p>
      )}
    </ChartCard>
  );
}

export { segmentKey };
